import { Link } from "react-router-dom";
import SafeImage from "@/components/base/SafeImage";
import { useFavorites } from "@/hooks/useFavorites";
import { getCategoryName } from "@/lib/catalog";
import type { Movie } from "@/lib/catalog";

type Props = {
  movie: Movie;
  rank?: number;
  showSynopsis?: boolean;
};

export default function MovieCard({ movie, rank, showSynopsis = true }: Props) {
  const { ids, toggle } = useFavorites();
  const saved = ids.includes(movie.id);

  return (
    <article className="group relative flex flex-col">
      <div className="relative w-full overflow-hidden rounded-lg bg-background-200">
        <Link to={`/detail/${movie.id}`} className="block aspect-[2/3] w-full cursor-pointer overflow-hidden">
          <SafeImage
            src={movie.poster}
            alt={`${movie.title} 影片封面`}
            title={`${movie.title} - 日本情色电影影片资料`}
            className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
          />
          <span className="pointer-events-none absolute inset-0 card-mask opacity-90" />
        </Link>

        <button
          type="button"
          onClick={() => toggle(movie.id)}
          aria-label={saved ? `取消收藏 ${movie.title}` : `收藏 ${movie.title}`}
          className={`absolute right-2 top-2 z-10 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border backdrop-blur transition-colors ${
            saved
              ? "border-primary-400 bg-primary-500 text-foreground-50"
              : "border-background-400/50 bg-background-50/70 text-foreground-200 hover:border-accent-400 hover:text-accent-400"
          }`}
        >
          <i className={saved ? "ri-heart-3-fill text-sm" : "ri-heart-3-line text-sm"} />
        </button>

        {typeof rank === "number" && (
          <span className="pointer-events-none absolute left-0 top-0 z-10 flex h-9 min-w-9 items-center justify-center bg-primary-500 px-2 font-display text-lg font-semibold text-foreground-50">
            {String(rank).padStart(2, "0")}
          </span>
        )}

        <div className="pointer-events-none absolute bottom-3 left-3 right-3 flex items-end justify-between gap-2">
          <span className="rounded-full bg-background-50/80 px-2 py-1 text-[10px] tracking-widest2 text-accent-300 font-label">
            {movie.year}
          </span>
          <span className="rounded-full bg-accent-500 px-2 py-1 font-label text-[11px] font-semibold text-background-50">
            {movie.rating.toFixed(1)}
          </span>
        </div>
      </div>

      <div className="mt-3">
        <Link to={`/detail/${movie.id}`} className="cursor-pointer">
          <h3 className="font-heading text-[15px] font-semibold leading-snug text-foreground-100 transition-colors group-hover:text-accent-400">
            {movie.title}
          </h3>
        </Link>
        <p className="mt-1 text-[11px] tracking-wide text-foreground-500 font-label">
          {movie.titleJa} · {movie.director}
        </p>
        {showSynopsis && (
          <p className="mt-2 line-clamp-2 text-[12px] leading-relaxed text-foreground-400">{movie.synopsis}</p>
        )}
        <p className="mt-2 truncate text-[11px] text-foreground-600 font-label">{getCategoryName(movie.categories)}</p>
      </div>
    </article>
  );
}